/**
 * @format
 * @flow
 */

import React from 'react';
import '../styles/ProductCartRow.scss'
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { updateProduct, updateCartTotal, deleteProduct } from '../actions/cartAction';
import sharedCartService from '../services/sharedCartService';
import { formatCurrency } from './Utils';
import { getTrans } from '../utils/translations';

const COMMENT_MAX_LENGTH = 100

class ProductCartRow extends React.Component {

  state = {
    editingComment: false,
    commentDraft: ''
  }

  componentDidUpdate(prevProps) {
    // Monitor cart products changes in shared mode (removed forceUpdate to prevent infinite loops)
  }

  deleteProduct = async () => {
    // Handle Firebase-based shared cart deletion
    if (this.props.settings.sharedCart && sharedCartService.isInSharedMode()) {
      try {
        await sharedCartService.removeProduct(this.props.index)
        // Firebase will automatically sync changes to Redux via listeners
        return
      } catch (error) {
        console.error('Error removing product from Firebase cart:', error)
        // Fall back to local deletion
      }
    }

    // Regular local cart deletion (not used in shared mode)
    await this.props.deleteProduct(this.props.index)
    await this.getCartPrice()
  }

  updateProduct = async (newQuantity) => {
    const { product, index } = this.props
    const { total, totalHT, totalVat, subtotal } = this.getProductPrice(product, newQuantity, false)
    const newProduct = {
      ...product,
      quantity: newQuantity,
      discount: product.discount || null,
      tableDiscount: product.tableDiscount || null,
      takeawayDiscount: product.takeawayDiscount || null,
      deliveryDiscount: product.deliveryDiscount || null,
      vat: product.vat,
      rubric: product.rubric,
      total,
      totalHT,
      totalVat,
      subtotal,
    }

    // Handle Firebase-based shared cart update
    if (this.props.settings.sharedCart && sharedCartService.isInSharedMode()) {
      try {
        await sharedCartService.updateProduct(index, newProduct)
        // Firebase will automatically sync changes to Redux via listeners
        return
      } catch (error) {
        console.error('Error updating product in Firebase cart:', error)
        // Fall back to local update
      }
    }

    // Regular local cart update (not used in shared mode)
    await this.props.updateProduct(newProduct, index)
    await this.getCartPrice()
  }

  startEditComment = (e) => {
    e.stopPropagation()
    const comment = typeof this.props.product.comment === 'string' ? this.props.product.comment : ''
    this.setState({ editingComment: true, commentDraft: comment.slice(0, COMMENT_MAX_LENGTH) })
  }

  cancelEditComment = (e) => {
    e.stopPropagation()
    this.setState({ editingComment: false, commentDraft: '' })
  }

  saveComment = async (e) => {
    e.stopPropagation()
    const { product, index } = this.props
    const comment = (this.state.commentDraft || '').trim().slice(0, COMMENT_MAX_LENGTH)
    this.setState({ editingComment: false, commentDraft: '' })
    if (comment === (product.comment || '')) return
    const newProduct = { ...product, comment }

    // Panier partagé : mise à jour via Firebase (le prix ne change pas)
    if (this.props.settings.sharedCart && sharedCartService.isInSharedMode()) {
      try {
        await sharedCartService.updateProduct(index, newProduct)
        return
      } catch (error) {
        console.error('Error updating product comment in Firebase cart:', error)
      }
    }

    await this.props.updateProduct(newProduct, index)
  }

  renderComment = () => {
    const { product, settings, t } = this.props
    const comment = typeof product.comment === 'string' ? product.comment : ''
    const canEdit = settings.productComments === true && this.props.editable !== false
    if (!canEdit && comment === '') return null

    if (this.state.editingComment) {
      const draft = this.state.commentDraft
      return (
        <div className="cart-comment editing" onClick={(e) => e.stopPropagation()}>
          <textarea
            className="cart-comment-input"
            rows="2"
            maxLength={COMMENT_MAX_LENGTH}
            placeholder={t('product.commentPlaceholder')}
            value={draft}
            autoFocus
            onChange={(e) => this.setState({ commentDraft: e.target.value.slice(0, COMMENT_MAX_LENGTH) })}
          />
          <div className="cart-comment-actions">
            <span className="cart-comment-counter">{draft.length}/{COMMENT_MAX_LENGTH}</span>
            <button type="button" className="cart-comment-btn" onClick={this.cancelEditComment}>{t('common.cancel')}</button>
            <button type="button" className="cart-comment-btn primary" onClick={this.saveComment}>{t('common.ok')}</button>
          </div>
        </div>
      )
    }

    return (
      <div className="cart-comment" onClick={(e) => e.stopPropagation()}>
        {comment !== '' && (
          <span className="cart-comment-text">{t('product.commentDisplay', { comment })}</span>
        )}
        {canEdit && (
          <button type="button" className="cart-comment-btn link" onClick={this.startEditComment}>
            {comment !== '' ? t('product.commentEdit') : t('product.commentAdd')}
          </button>
        )}
      </div>
    )
  }

  getCartPrice = () => {
    const cart = this.props.cart
    if (cart.products.length === 0) {
      this.props.updateCartTotal(0, 0, 0, 0, 0)
      return
    }
    let cartTotal = 0
    let cartTotalHT = 0
    let cartTotalVat = 0
    let cartTotalProducts = 0
    let cartSubtotal = 0
    let cartSubtotalWithoutDiscount = this.getCartSubtotal(cart.products)
    let applyDiscount = false
    if (this.props.cart.orderType === 1 && cartSubtotalWithoutDiscount >= (this.props.settings.takeawaySettings.discountFrom ?? 0)) {
      applyDiscount = true
    } else if (this.props.cart.orderType === 4 && cartSubtotalWithoutDiscount >= (this.props.settings.deliverySettings.discountFrom ?? 0)) {
      applyDiscount = true
    }

    cart.products.forEach((product) => {
      const { total, totalHT, totalVat, subtotal } = this.getProductPrice(product, product.quantity, true, applyDiscount)
      cartTotal += total
      cartTotalHT += totalHT
      cartTotalVat += totalVat
      cartTotalProducts += product.quantity
      cartSubtotal += subtotal
    })

    // Update totals in Redux
    this.props.updateCartTotal(cartTotal, cartTotalHT, cartTotalVat, cartTotalProducts, cartSubtotal)

    // Sync totals to Firebase if in shared mode
    if (this.props.settings.sharedCart && sharedCartService.isInSharedMode()) {
      try {
        sharedCartService.updateCartTotals({
          total: cartTotal,
          totalHT: cartTotalHT,
          totalVat: cartTotalVat,
          totalProducts: cartTotalProducts,
          subtotal: cartSubtotal
        })
      } catch (error) {
        console.error('Error syncing totals to Firebase:', error)
      }
    }
  }

  getCartSubtotal = (products) => {
    const orderType = this.props.cart.orderType;
    let cartSubtotalWithoutDiscount = 0
    products.forEach((product) => {
      cartSubtotalWithoutDiscount += product.quantity * product.unitPrice
      product.rubric.forEach((section) => {
        section.data.forEach((rubric) => {
          if (rubric.checked && rubric.unitPrice > 0) {
            if (section.allowSameMultiple) {
              cartSubtotalWithoutDiscount += product.quantity * rubric.unitPrice * (rubric.quantity || 1)
            }
            else {
              cartSubtotalWithoutDiscount += product.quantity * rubric.unitPrice
            }
          }
        })
      })
      if (orderType === 1 && product.takeawayDiscount && product.takeawayDiscount.discount) {
        cartSubtotalWithoutDiscount = cartSubtotalWithoutDiscount * (1 - product.takeawayDiscount.discount / 100)
      }
      else if (orderType === 4 && product.deliveryDiscount && product.deliveryDiscount.discount) {
        cartSubtotalWithoutDiscount = cartSubtotalWithoutDiscount * (1 - product.deliveryDiscount.discount / 100)
      }
      else if ((orderType === 2 || orderType === 3) && product.tableDiscount && product.tableDiscount.discount) {
        cartSubtotalWithoutDiscount = cartSubtotalWithoutDiscount * (1 - product.tableDiscount.discount / 100)
      }
    })
    return cartSubtotalWithoutDiscount
  }

  getProductPrice = (product: ProductType, quantity: number, forTotal: boolean, applyDiscount: boolean): { total: number, totalHT: number, totalVat: number, subtotal: number } => {
    const rubric = product.rubric
    const orderType = this.props.cart.orderType;

    if (typeof product === 'undefined') return { total: 0, totalHT: 0, totalVat: 0 }
    let total = quantity * product.unitPrice
    let totalHT = quantity * product.unitPriceHT


    if (forTotal) {
      rubric.forEach((section) => {
        section.data.forEach((rubric) => {
          if (rubric.checked && rubric.unitPrice > 0) {
            if (section.allowSameMultiple) {
              total += quantity * rubric.unitPrice * (rubric.quantity || 1)
              totalHT += quantity * rubric.unitPriceHT * (rubric.quantity || 1)
            }
            else {
              total += quantity * rubric.unitPrice
              totalHT += quantity * rubric.unitPriceHT
            }
          }
        })
      })
    }

    // apply product discount
    if (orderType === 1 && product.takeawayDiscount && product.takeawayDiscount.discount) {
      total = total * (1 - product.takeawayDiscount.discount / 100)
      totalHT = totalHT * (1 - product.takeawayDiscount.discount / 100)
    }
    else if (orderType === 4 && product.deliveryDiscount && product.deliveryDiscount.discount) {
      total = total * (1 - product.deliveryDiscount.discount / 100)
      totalHT = totalHT * (1 - product.deliveryDiscount.discount / 100)
    }
    else if ((orderType === 2 || orderType === 3) && product.tableDiscount && product.tableDiscount.discount) {
      total = total * (1 - product.tableDiscount.discount / 100)
      totalHT = totalHT * (1 - product.tableDiscount.discount / 100)
    }

    let subtotal = total

    // apply cart discount
    if (this.props.cart.discount && applyDiscount) {
      if (
        (orderType === 1 && (!product.takeawayDiscount || (product.takeawayDiscount && product.takeawayDiscount.canDiscount))) ||
        (orderType === 4 && (!product.deliveryDiscount || (product.deliveryDiscount && product.deliveryDiscount.canDiscount))) ||
        ((orderType === 2 || orderType === 3) && (!product.tableDiscount || (product.tableDiscount && product.tableDiscount.canDiscount)))
      ) {
        total = total * (1 - this.props.cart.discount / 100)
        totalHT = totalHT * (1 - this.props.cart.discount / 100)
      }
    }

    // if (typeof product.discount !== 'undefined' && product.discount !== null && !product.discount.isFidelityDiscount) {
    //   total = total * (1 - product.discount.percentage / 100)
    //   totalHT = totalHT * (1 - product.discount.percentage / 100)
    // }

    let totalVat = total - totalHT
    return { total, totalHT, totalVat, subtotal }
  }


  handleQuantityChange = (event) => {
    let newQuantity = parseInt(event.target.value)
    if (newQuantity === this.props.product.quantity) return
    else if (newQuantity === 0) {
      this.deleteProduct()
    }
    else if (newQuantity !== this.props.product.quantity) {
      this.updateProduct(newQuantity)
    }
  }

  render() {
    const lang = this.props.settings.lang
    return (
      <div className="cart-product-item" onClick={this.props.onClick(this.props.index)}>
        <div className="cart-product-row">
          <div className="cart-row">
            <div className="text-number-product">
              {this.props.editable === false && (
                this.props.product.quantity
              )}
              {(this.props.editable === undefined || this.props.editable === true) && (
                <select className="select select-bordered select-sm max-w-xs" value={this.props.product.quantity} onClick={(e) => { e.stopPropagation(); }} onChange={this.handleQuantityChange}>
                  {Array.apply(null, Array(50)).map((o, idx) =>
                    <option key={idx} value={idx}>{idx}</option>
                  )}
                </select>
              )}

            </div>
            <div className="cart-text">{getTrans(this.props.product, 'name', lang)}</div>
          </div>
          <div className="cart-text">{formatCurrency(this.props.product.subtotal, this.props.settings.currencySymbol)}</div>
        </div>
        {this.props.product.rubric?.map((rubric, index) => {
          if (rubric.data.length > 0)
            return (
              <div key={index} className="cart-rubric-list">
                {rubric.data?.map((r, index) => {
                  if (r.checked)
                    return (
                      <div className="rubric-text flex justify-between" key={index}>
                        <div className="text-left">
                          {getTrans(r, 'name', lang)} {r.unitPrice > 0 ? '(' + formatCurrency(r.unitPrice, this.props.settings.currencySymbol) + ')' : ''} {r.quantity && 'x' + r.quantity}
                        </div>
                        <div className="text-right">
                          {r.unitPrice > 0 && formatCurrency(this.props.product.quantity * r.unitPrice * (r.quantity || 1), this.props.settings.currencySymbol)}
                        </div>
                      </div>
                    );
                  return null
                })}
              </div>
            );
          else return null;
        })}
        {this.renderComment()}
      </div>
    )
  }
}

// redux
const mapStateToProps = (state) => {
  return {
    cart: state.cart,
    settings: state.settings
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    updateProduct: (product, idx) => dispatch(updateProduct(product, idx)),
    updateCartTotal: (cartTotal, cartTotalHT, cartTotalVat, cartTotalProducts, cartSubtotal) => dispatch(updateCartTotal(cartTotal, cartTotalHT, cartTotalVat, cartTotalProducts, cartSubtotal)),
    deleteProduct: (idx) => dispatch(deleteProduct(idx))
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ProductCartRow));